// Faq.js
import React from 'react';

function Faq() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // U praksi kod tebe role mogu biti: admin/org_admin/teacher/parent/super_admin itd.
  const role = user.role || 'teacher';
  const roleLabel = (() => {
    const r = String(role || '').toLowerCase();
    if (r.includes('super')) return 'Super Admin';
    if (r.includes('org')) return 'Org Admin';
    if (r === 'admin') return 'Admin';
    if (r === 'parent') return 'Parent';
    return 'Teacher / Provider';
  })();

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-xl font-bold mb-2">FAQ – VisitOMS EarlyCare</h1>
      <p className="text-sm text-gray-600 mb-4">
        Here you can find answers to common questions about the VisitOMS EarlyCare platform.
        This page is especially useful for <span className="font-medium">Org Admins</span> who are setting up the system and guiding
        teachers and parents during onboarding.
      </p>

      {/* QUICK START */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">Org Admin – Quick Start (first 30–60 minutes)</h2>

        <details className="border rounded-md p-3" open>
          <summary className="font-medium cursor-pointer">
            What should I configure first after onboarding?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              A good order for first setup:
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>
                <span className="font-medium">Organization Settings</span> – timezone, service types (EI/PI/ABA/OT/PT), visit rules,
                telehealth defaults, and internal policies.
              </li>
              <li>
                <span className="font-medium">Teachers</span> – add teachers/providers, set training stage (none/shadow/supervised),
                hourly rate, and contact details.
              </li>
              <li>
                <span className="font-medium">Students + Parents</span> – import/register students, connect parents, set default teacher(s),
                addresses (primary + alternative).
              </li>
              <li>
                <span className="font-medium">Availability</span> – ensure teachers have availability configured (weekly + exceptions).
              </li>
              <li>
                <span className="font-medium">Visits Scheduling</span> – schedule the week, verify routing suggestions and conflicts.
              </li>
              <li>
                <span className="font-medium">Messaging + Notifications</span> – confirm that admin/teacher/parent communication works.
              </li>
            </ol>
            <p className="text-xs text-gray-500">
              Tip: after this, your teachers can fully operate with: My Day Planner → My Visits → Skills/Notes → Reports.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What is the core workflow in one sentence?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Admin schedules visits (and routing), teachers execute visits (arrival/check-in/check-out, notes, skills),
            parents can confirm/communicate, and the system produces audit logs, time logs, and reports for compliance and billing.
          </p>
        </details>
      </section>

      {/* GENERAL */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">General</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What is VisitOMS EarlyCare?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            VisitOMS EarlyCare is a platform for managing home-based visits for children aged 0–36 months across programs like EI and PI
            (and optionally ABA/OT/PT). It integrates scheduling, routing, teacher availability, geolocation/time logging, skill tracking,
            reporting, document management, messaging, and telehealth in one workflow.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Who uses the system?
          </summary>
          <p className="text-sm text-gray-700 mt-2">The system is used by:</p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            <li>Organization administrators/coordinators (Org Admin / Admin roles).</li>
            <li>Teachers/providers/specialists (Teacher role).</li>
            <li>Parents/guardians (Parent role) – optional, depending on your organization policy.</li>
          </ul>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What do EI and PI programs mean in this context?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            EI stands for Early Intervention (0–36 months). PI stands for <span className="font-medium">Project Impact</span>.
            Each child can have a program type and the system uses it to drive planning, discipline badges, eligibility, and reporting.
          </p>
        </details>
      </section>

      {/* ADMIN / ORG ADMIN */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">For Org Admins / Administrators</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I add or edit teachers (including training/mentor info)?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Use <span className="font-medium">Manage Teachers</span>. You can create teachers, edit contact details, address, internal notes,
              rate/bonus settings, and training info.
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <span className="font-medium">Training Stage</span>: none / shadow / supervised.
              </li>
              <li>
                <span className="font-medium">Mentor Teacher</span>: optional mentor assignment for trainees.
              </li>
              <li>
                <span className="font-medium">Active/Inactive</span>: controls whether the teacher is available for scheduling and login (policy-dependent).
              </li>
            </ul>
            <p className="text-xs text-gray-500">
              Tip: trainees (shadow/supervised) can be excluded from auto-suggestions and routing logic depending on your training policy.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I register a new child and connect parents?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Go to <span className="font-medium">Manage Students</span>. You can enter child demographics, program type (EI/PI/etc),
              addresses (primary + alternative), default teacher, and parent information.
            </p>
            <p>
              Parents can be connected by email. If your configuration allows it, parent accounts can log in and see relevant content
              (e.g., visit confirmations, messages, documents, progress summaries) based on permissions.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I schedule visits for a child (and handle substitutions)?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Use <span className="font-medium">Manage Visits</span> for weekly planning of one-hour slots per child.
              The system suggests the default teacher when available. If they are not available, the system can propose substitutes
              based on availability + conflicts + distance + estimated cost.
            </p>
            <p>
              Substitute logic typically preserves original assignment context (default teacher) while allowing another teacher to run that visit.
              Statuses like <span className="font-medium">pending / accepted / rejected / failed</span> may appear depending on your workflow.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How is teacher availability handled (weekly + exceptions + days off)?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Teachers manage weekly availability and exceptions in <span className="font-medium">My Availability</span>.
              As an admin, you can review or override in <span className="font-medium">Teachers Availability</span>.
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="font-medium">Weekly availability</span> – typical weekly schedule.</li>
              <li><span className="font-medium">Exceptions</span> – special changes for specific dates/weeks.</li>
              <li><span className="font-medium">Days off</span> – full day unavailable.</li>
            </ul>
            <p className="text-xs text-gray-500">
              Scheduling respects these rules and avoids double booking and conflicts.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Where can I see audits, activity history, and exports?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Many admin pages include <span className="font-medium">Audit Log</span> or history modals for accountability.
              You can also use CSV import/export for teachers/students depending on enabled modules.
            </p>
            <p className="text-xs text-gray-500">
              Audit logging is important for compliance and operational transparency (who changed what and when).
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What settings are typically available for Org Admin?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>Common settings include:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="font-medium">Organization profile</span>: name, address, timezone.</li>
              <li><span className="font-medium">Programs & services</span>: EI/PI/ABA/OT/PT toggles, discipline rules/badges.</li>
              <li><span className="font-medium">Visit rules</span>: default duration, grace periods, allowed statuses.</li>
              <li><span className="font-medium">Telehealth policy</span>: allowed/required flags, documentation expectations.</li>
              <li><span className="font-medium">Messaging policy</span>: who can message whom (admin↔teacher↔parent), retention rules.</li>
              <li><span className="font-medium">Notifications</span>: email/app prompts for substitutions, visit reminders, parent confirmations.</li>
              <li><span className="font-medium">Security</span>: session behavior, role permissions, access restrictions.</li>
            </ul>
          </div>
        </details>
      </section>

      {/* MESSAGING */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">Messages & Communication</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What is the Messages module and how is it used?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Messages provide a “Viber-like” internal communication channel between roles (depending on your org policy):
              Admin ↔ Teacher, Teacher ↔ Parent, and sometimes Admin ↔ Parent.
            </p>
            <p>
              It is commonly used for: schedule changes, arrival delays, telehealth links, missing documents, quick clarifications,
              and parent confirmations.
            </p>
            <p className="text-xs text-gray-500">
              Tip: keep sensitive discussions inside the platform to preserve context and auditability.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Do messages trigger notifications?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Depending on your organization settings, messages can trigger email notifications or in-app indicators.
            Some deployments also support reminder digests for unread messages.
          </p>
        </details>
      </section>

      {/* TELEHEALTH */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">Telehealth</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What is a telehealth visit in the system?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Telehealth visits are remote sessions (video/phone) that are scheduled and tracked similarly to regular visits, but may have:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Different check-in expectations (no physical arrival needed).</li>
              <li>Different documentation requirements (notes, goals, confirmations).</li>
              <li>Optional telehealth link or call reference shared through Messages.</li>
            </ul>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Can telehealth be mixed with in-person scheduling?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Yes. Telehealth can be scheduled alongside in-person visits. The day planner UI often shows telehealth indicators so the teacher
            can prepare (no travel time, different checklist, etc.).
          </p>
        </details>
      </section>

      {/* OFFLINE */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">Offline Mode & Connectivity</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What is Offline Queue and why does it matter?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              In home visits, internet can drop at any moment. Offline Queue allows the app to temporarily store critical actions
              (like check-in/check-out/notes) and send them automatically when the connection returns.
            </p>
            <p>
              This reduces the risk of losing work if the teacher leaves the location without realizing the device was offline.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What should teachers do if they suspect poor connection?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Teachers should keep the app open during check-out, and if the system shows “offline” indicators, wait for sync or
            confirm later that the visit is saved. Offline Queue helps, but good practice is still important.
          </p>
        </details>
      </section>

      {/* TRAINING */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">Mentor & Trainee Program</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do shadow/supervised stages work?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Training stages help you onboard new providers safely:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="font-medium">Shadow</span>: trainee observes workflows; may have limited permissions.</li>
              <li><span className="font-medium">Supervised</span>: trainee performs tasks with mentor oversight.</li>
              <li><span className="font-medium">None</span>: fully active teacher.</li>
            </ul>
            <p>
              Mentors can be assigned per trainee so admins can monitor support structure and performance.
            </p>
          </div>
        </details>
      </section>

      {/* PARENTS */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">Parents / Family Access</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What can parents typically do in the system?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              Parent access depends on org configuration. Common capabilities include:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Messaging with teachers/admins.</li>
              <li>Viewing selected documents (shared by the agency).</li>
              <li>Confirmations (when enabled) and basic schedule visibility.</li>
              <li>Receiving telehealth links and instructions.</li>
            </ul>
            <p className="text-xs text-gray-500">
              Tip: decide early what parents can view to keep workflows consistent and compliant.
            </p>
          </div>
        </details>
      </section>

      {/* TEACHER SECTION */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">For Teachers / Providers</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What should I do first when I log in?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Check <span className="font-medium">My Day Planner</span> and <span className="font-medium">My Visits</span>.
            Ensure geolocation is enabled if your organization requires arrival/check-in/out based on GPS.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I record arrival, check-in, and check-out?
          </summary>
          <div className="text-sm text-gray-700 mt-2 space-y-2">
            <p>
              In <span className="font-medium">My Visits</span> or visit detail view:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="font-medium">Arrival</span> – confirms you are near the child’s address.</li>
              <li><span className="font-medium">Check-in</span> – starts the visit (timestamp + optional geolocation).</li>
              <li><span className="font-medium">Check-out</span> – ends the visit (timestamp + optional geolocation).</li>
            </ul>
            <p className="text-xs text-gray-500">
              Some organizations may use additional confirmations (e.g., parent confirmation / QR / signature) as part of compliance.
            </p>
          </div>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            How do I update skills, notes, and reporting content?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Use <span className="font-medium">My Students Skills</span> to update baseline and progress, and review history.
            Reports can include goals/achievements and summaries depending on the reporting period and your organization policy.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Where can I see my hours and time logs?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Use <span className="font-medium">My Time Log</span> to review worked hours and visit activity.
            Admins can use this for payroll verification and operational planning.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What if geolocation is blocked or inaccurate?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Enable location permissions for your browser/app. If GPS is weak (indoors/no signal), continue working and record notes.
            Some time/route calculations may fall back to scheduled values depending on settings.
          </p>
        </details>
      </section>

      {/* TROUBLESHOOTING */}
      <section className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-xl font-semibold mb-2">Troubleshooting</h2>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            I was logged out / session expired. What should I do?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Simply log in again. If you were completing a visit, check whether actions were synced (especially if you were offline).
            In many setups, Offline Queue helps preserve work until sync is possible.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            Why didn’t a notification email send (messages, substitutions, reminders)?
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Check your org notification settings and confirm the target user email is correct. Some notifications are batched or run on scheduled intervals.
            If using clustered backend, scheduled tasks should run on a single leader instance to prevent duplicates.
          </p>
        </details>

        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            A teacher reports missing visits or schedule mismatch
          </summary>
          <p className="text-sm text-gray-700 mt-2">
            Verify (1) teacher availability/exceptions, (2) week filter/date range, (3) substitution status, and (4) whether the visit was created as telehealth vs in-person.
            Admin views like “All Visits” can help confirm what is scheduled vs completed vs canceled.
          </p>
        </details>
      </section>

      {/* Small note depending on role */}
      <p className="text-xs text-gray-500 text-center mt-4">
        You are currently logged in as: <span className="font-medium">{roleLabel}</span>. Some menus, labels, and permissions may differ slightly
        depending on your organization configuration.
      </p>
    </div>
  );
}

export default Faq;


